/**
 * Banda de testimonios con crossfade — DominandoTuNegocio.cl
 *
 * Las diapositivas son los HIJOS DIRECTOS del track (content_for "blocks"
 * envuelve cada bloque anidado). Solo una lleva .is-active a la vez.
 */
class DtnTestimonials extends HTMLElement {
  #timer = null;
  #paused = false;
  #index = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-dtn-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.dotsWrap = this.querySelector('[data-dtn-dots]');
    this.prevBtn = this.querySelector('[data-dtn-prev]');
    this.nextBtn = this.querySelector('[data-dtn-next]');

    if (this.slides.length === 0) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#buildDots();
    this.#bind();
    this.#show(0);
    this.#maybeStart();
  }

  disconnectedCallback() {
    this.#stop();
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.textContent = '';
    if (this.slides.length < 2) return;

    this.dots = this.slides.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'dtn-t__dot';
      dot.setAttribute('aria-label', `Testimonio ${i + 1}`);
      dot.addEventListener('click', () => {
        this.#show(i);
        this.#restart();
      });
      this.dotsWrap.appendChild(dot);
      return dot;
    });
  }

  #bind() {
    this.prevBtn?.addEventListener('click', () => {
      this.#show(this.#index - 1);
      this.#restart();
    });

    this.nextBtn?.addEventListener('click', () => {
      this.#show(this.#index + 1);
      this.#restart();
    });

    this.addEventListener('pointerenter', () => (this.#paused = true));
    this.addEventListener('pointerleave', () => (this.#paused = false));
    this.addEventListener('focusin', () => (this.#paused = true));
    this.addEventListener('focusout', () => (this.#paused = false));

    document.addEventListener('visibilitychange', this.#onVisibility);

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === this) this.#paused = !entry.isIntersecting;
        }
      },
      { threshold: 0.2 }
    );
    this.observer.observe(this);
  }

  #onVisibility = () => {
    this.#paused = document.hidden;
  };

  #show(index) {
    const max = this.slides.length - 1;
    let next = index;
    if (next < 0) next = max;
    if (next > max) next = 0;

    this.#index = next;

    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    if (this.dots) this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === next));
  }

  #maybeStart() {
    if (this.dataset.autoplay !== 'true') return;
    if (this.reduced) return;
    if (this.slides.length < 2) return;
    this.#start();
  }

  #start() {
    const speed = Number(this.dataset.speed) || 5000;
    this.#timer = setInterval(() => {
      if (this.#paused) return;
      this.#show(this.#index + 1);
    }, speed);
  }

  #stop() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }

  #restart() {
    this.#stop();
    this.#maybeStart();
  }
}

if (!customElements.get('dtn-testimonials')) {
  customElements.define('dtn-testimonials', DtnTestimonials);
}